/**
 * Lead attribution (where a lead came from). Every lead type carries a normalized
 * `source` channel and a `meta` object; the backend (leads.LeadSource /
 * leads.AttributionKeys) writes:
 *
 *   source: `linkedin` | `google` | `meta` | `chatgpt` | `referral:<host>` | `direct`
 *           | a slugged utm_source naming no known channel | '' (leads before capture)
 *   meta:   the allow-listed first-touch keys below, plus `user_agent` and `ip`.
 *
 * `ip` is personal data and is deliberately never surfaced here. Values are rendered as
 * text only (never as links), since they come from the visitor's browser.
 */

export interface AttributionField {
  /** Key inside the lead's `meta` object. */
  key: string;
  /** i18n key for the label, resolved at render. */
  labelKey: string;
}

/** Attribution fields, in display order. Mirrors backend AttributionKeys + user_agent. */
export const ATTRIBUTION_FIELDS: readonly AttributionField[] = [
  { key: 'utm_source', labelKey: 'leads.source.fields.utmSource' },
  { key: 'utm_medium', labelKey: 'leads.source.fields.utmMedium' },
  { key: 'utm_campaign', labelKey: 'leads.source.fields.utmCampaign' },
  { key: 'utm_content', labelKey: 'leads.source.fields.utmContent' },
  { key: 'utm_term', labelKey: 'leads.source.fields.utmTerm' },
  { key: 'li_fat_id', labelKey: 'leads.source.fields.liFatId' },
  { key: 'gclid', labelKey: 'leads.source.fields.gclid' },
  { key: 'fbclid', labelKey: 'leads.source.fields.fbclid' },
  { key: 'landing_path', labelKey: 'leads.source.fields.landingPath' },
  { key: 'referrer', labelKey: 'leads.source.fields.referrer' },
  { key: 'user_agent', labelKey: 'leads.source.fields.userAgent' },
];

/** Channels the backend names explicitly; each has a translated label. */
export const KNOWN_CHANNELS = ['linkedin', 'google', 'meta', 'chatgpt', 'direct'] as const;
type KnownChannel = (typeof KNOWN_CHANNELS)[number];

const REFERRAL_PREFIX = 'referral:';

/** A lead's `source`, classified for display and filtering. */
export type SourceChannel =
  | { kind: 'known'; channel: KnownChannel }
  | { kind: 'referral'; host: string }
  | { kind: 'other'; value: string }
  | { kind: 'none' };

export function classifySource(raw: unknown): SourceChannel {
  const source = typeof raw === 'string' ? raw.trim() : '';
  if (!source) return { kind: 'none' };
  if ((KNOWN_CHANNELS as readonly string[]).includes(source)) {
    return { kind: 'known', channel: source as KnownChannel };
  }
  if (source.startsWith(REFERRAL_PREFIX)) {
    return { kind: 'referral', host: source.slice(REFERRAL_PREFIX.length) };
  }
  return { kind: 'other', value: source };
}

/** Client-side source filter values (the list API has no server-side source filter). */
export const SOURCE_FILTERS = [...KNOWN_CHANNELS, 'referral', 'other', 'none'] as const;
export type SourceFilter = (typeof SOURCE_FILTERS)[number];

export function isSourceFilter(v: string): v is SourceFilter {
  return (SOURCE_FILTERS as readonly string[]).includes(v);
}

/** True when a lead's `source` falls under the given filter; '' matches everything. */
export function matchesSource(raw: unknown, filter: SourceFilter | ''): boolean {
  if (!filter) return true;
  const c = classifySource(raw);
  switch (c.kind) {
    case 'known':
      return c.channel === filter;
    case 'referral':
      return filter === 'referral';
    case 'other':
      return filter === 'other';
    case 'none':
      return filter === 'none';
  }
}

/**
 * Reads one attribution value off a lead's `meta`. Only non-empty strings count: `meta`
 * is free-form JSONB, so arrays, objects and other shapes yield ''.
 */
export function readMeta(meta: unknown, key: string): string {
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return '';
  const v = (meta as Record<string, unknown>)[key];
  return typeof v === 'string' ? v.trim() : '';
}
